import { DateTime } from "luxon";
import type { RecurringShiftWeekPrefill } from "../../../../components/RecurringShift/RecurringShiftMasterSchedule/utils";
import { getDayNameByIndex } from "../../../../utils/utilFunctions";
import { dayIdxOf, type SuggestedHireDraft, weekIdxOf } from "./suggestedHires";

/**
 * Turns a suggested hire into the v2 Recurring Shift panel's `prefilledWeeks`, so the user doesn't
 * retype a schedule the module just proposed.
 *
 * Nothing here writes anything: the panel is the app's existing create flow and the hire is a
 * hypothetical person, so this is purely "open that panel with these answers filled in".
 *
 * PER WEEK, not unioned. The panel models a two-week template as one row per template week, so a
 * hire working Mon/Wed in week 1 and Mon/Fri in week 2 produces TWO entries — one per week — and
 * lands as `Week 1 (…) Mon,Wed` / `Week 2 (…) Mon,Fri`. The previous prefill targeted the legacy
 * panel, whose single weekly recurrence had no per-week variation to carry, so it collapsed the two
 * weeks into one Mon/Wed/Fri row and also had to drop every shift type but the most-worked one.
 * Neither compromise applies here.
 */

/**
 * The two shapes the backend's `format_clock` can emit — `7am` on the hour, `2:30pm` otherwise.
 * Both are tried because a shift label pairs two independently-formatted clocks (`7am-3:30pm`).
 */
const CLOCK_FORMATS = ["h:mma", "ha"] as const;

/** The panel's own wire format for a shift's times. */
const PANEL_CLOCK_FORMAT = "HH:mm";

const parseClock = (clock: string): string | undefined => {
	for (const format of CLOCK_FORMATS) {
		const parsed = DateTime.fromFormat(clock.trim(), format);
		if (parsed.isValid) {
			return parsed.toFormat(PANEL_CLOCK_FORMAT);
		}
	}
	return undefined;
};

/**
 * A shift label (`7am-3pm`, `11pm-7:30am`) into the `HH:mm` pair the panel holds.
 *
 * Returns `undefined` rather than guessing when the label isn't a clock pair — an overnight pair
 * needs no special case here, because the panel derives "overnight" from `endTime <= startTime`
 * itself rather than from a calendar date.
 *
 * When it does return `undefined` the days are still pre-filled and the row simply keeps the panel's
 * own new-shift default times, which is what the user would have got from `+ Add Shifts`. Inventing
 * a time on a schedule someone is about to save is worse than handing them the panel's default, and
 * dropping the day entirely is worse than both.
 */
export const parseShiftLabelTimes = (label: string): { startTime: string; endTime: string } | undefined => {
	const parts = label.split("-");
	if (parts.length !== 2) {
		return undefined;
	}
	const startTime = parseClock(parts[0]);
	const endTime = parseClock(parts[1]);
	if (startTime === undefined || endTime === undefined) {
		return undefined;
	}
	return { startTime, endTime };
};

/** `weekdays` in the vocabulary `WeeklyRecurring` uses; the hire grid's `dayIdx` is Sunday-first too. */
const weekdayNameOf = (dayIdx: number): string => getDayNameByIndex(dayIdx).toLowerCase();

/**
 * One prefill entry per (template week × shift type) the hire works.
 *
 * Splitting on the shift type as well as the week is what lets a person who covers two templates —
 * a `7a-3p` Monday and an `11p-7a` Friday — arrive as two rows with their own times, instead of
 * being flattened onto whichever type they worked most. Each row is a `Weekly` recurrence at the
 * department's template interval, which is the only shape a per-week difference can take.
 *
 * Entries are ordered by week then by the hire's own chronological `coverShifts`, so the panel's two
 * columns read top-to-bottom in the same order the row's day chips do.
 */
export const hirePrefillWeeks = (hire: SuggestedHireDraft, jobId: string): RecurringShiftWeekPrefill[] => {
	const daysByWeekAndLabel = new Map<string, { weekIndex: number; label: string; dayIndexes: number[] }>();
	for (const [key, label] of Object.entries(hire.shiftByDay)) {
		const weekIndex = weekIdxOf(Number(key));
		const groupKey = `${weekIndex}|${label}`;
		const group = daysByWeekAndLabel.get(groupKey) ?? { weekIndex, label, dayIndexes: [] };
		group.dayIndexes.push(dayIdxOf(Number(key)));
		daysByWeekAndLabel.set(groupKey, group);
	}
	return [...daysByWeekAndLabel.values()]
		.sort((a, b) => a.weekIndex - b.weekIndex || hire.coverShifts.indexOf(a.label) - hire.coverShifts.indexOf(b.label))
		.map(({ weekIndex, label, dayIndexes }) => {
			// Spreading `parseShiftLabelTimes(...)` inline typed both times as independently optional, which
			// is exactly what `RecurringShiftWeekPrefill`'s both-or-neither union now rejects. Naming the
			// result keeps the pair together: a label we can read gives both times, and one we cannot gives
			// neither, so the panel falls back to its own new-shift defaults rather than half of ours.
			const times = parseShiftLabelTimes(label);
			const base = { weekIndex, weekdays: [...dayIndexes].sort((a, b) => a - b).map(weekdayNameOf), jobId };
			return times === undefined ? base : { ...base, ...times };
		});
};

/**
 * What the prefill hands over, for the `hiring_suggested_hire_recurring_opened` event: the hire's
 * shift types (their own labels, not the parsed clocks), every day, and how many template weeks it
 * spans. Reported over the whole week now that the panel takes all of it — the old payload named a
 * single "dominant" shift because that was all the legacy panel could carry.
 */
export const prefillSummary = (hire: SuggestedHireDraft): { shifts: string; days: number; weeks: number } => {
	const keys = Object.keys(hire.shiftByDay);
	return {
		shifts: [...new Set(Object.values(hire.shiftByDay))].join(","),
		days: keys.length,
		weeks: new Set(keys.map((key) => weekIdxOf(Number(key)))).size,
	};
};

/**
 * What a close of the Recurring Shift panel means for this page.
 *
 * The panel's `onClose` reports `didUpdateOrCreate` — `true` only when a recurring shift was actually
 * written. Closing always drops the modal; only a WRITE asks the page to refetch, because only a
 * write changed the schedule template the gaps grid reads. A plain cancel refetches nothing.
 *
 * Lives beside the prefill because the two are the same handoff: `hirePrefillWeeks` is what the
 * module hands the panel on the way IN, and this is what it does with the panel's answer on the way
 * OUT. Here rather than in the panel component so the rule is assertable without importing a
 * data-fetching, provider-dependent subtree.
 */
export const applyRecurringPanelClose = ({
	didUpdateOrCreate,
	onClosed,
	onSaved,
}: {
	didUpdateOrCreate?: boolean;
	onClosed: () => void;
	onSaved: () => void;
}): void => {
	onClosed();
	if (didUpdateOrCreate === true) {
		onSaved();
	}
};
